import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Backdrop } from "@/components/IntersectMotif";
import { FinalCTA } from "@/pages/Home";
import { IMG, INVESTMENT_STAGES, INVESTMENT_CRITERIA, PRINCIPLES } from "@/lib/content";
import { useLang } from "@/lib/i18n";

export default function Investment() {
  const { t } = useLang();
  return (
    <>
      <PageHero eyebrow="Investment" title="Backing businesses with more than capital." image={IMG.abstractGold}
        subtitle="Capital paired with market access, relationships and the ability to execute." />

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.kafdAerial} opacity={0.1} />
        <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><Reveal><p className="eyebrow mb-6">{t("Investment Philosophy")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory">{t("Our value beyond capital.")}</h2></Reveal></div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg leading-relaxed text-soft-stone">
            <Reveal delay={0.15}><p>{t("We identify, evaluate and participate in high-potential ventures where strategic capital, Saudi market access and partnership development can accelerate growth. Strong companies become stronger when they gain access to the right markets and relationships.")}</p></Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-charcoal relative overflow-hidden">
        <Backdrop src={IMG.kingdomDay} opacity={0.1} />
        <div className="container-x relative z-10">
          <Reveal><p className="eyebrow mb-6">{t("Investment Themes & Stages")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory mb-14">{t("Where we participate.")}</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-primary-gold/10 border border-primary-gold/10">
            {INVESTMENT_STAGES.map((s, i) => (
              <Reveal key={s.title}><div className="card-lift bg-charcoal p-8 min-h-[200px] flex flex-col justify-between hover:bg-obsidian transition-colors" data-testid={`stage-${i}`}>
                <span className="text-primary-gold text-xs tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</span>
                <div><h3 className="text-xl font-light text-ivory mb-3">{t(s.title)}</h3><p className="text-sm leading-relaxed text-muted-text">{t(s.text)}</p></div>
              </div></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.geometric} opacity={0.08} />
        <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><Reveal><p className="eyebrow mb-6">{t("What We Look For")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("Investment criteria.")}</h2></Reveal></div>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="divide-y divide-white/5 border-t border-white/5">
              {INVESTMENT_CRITERIA.map((c, i) => (
                <Reveal key={c}><div className="flex items-center gap-6 py-5" data-testid={`criteria-${i}`}>
                  <span className="text-primary-gold text-xs tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg text-soft-stone">{t(c)}</span>
                </div></Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-charcoal relative overflow-hidden">
        <Backdrop src={IMG.riyadhHighway} opacity={0.1} />
        <div className="container-x relative z-10">
          <Reveal><p className="eyebrow mb-6">{t("Our Value Beyond Capital")}</p></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-primary-gold/10 border border-primary-gold/10 mt-6">
            {PRINCIPLES.map((p) => (
              <Reveal key={p.no}><div className="bg-charcoal p-8 min-h-[220px] flex flex-col justify-between hover:bg-obsidian transition-colors">
                <span className="text-primary-gold text-sm tracking-[0.25em]">{p.no}</span>
                <div><h3 className="text-sm tracking-wide uppercase text-ivory mb-3">{t(p.title)}</h3><p className="text-sm leading-relaxed text-muted-text">{t(p.text)}</p></div>
              </div></Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12"><Link to="/opportunity" className="btn-gold" data-testid="investment-submit">{t("Submit an Opportunity")} <ArrowRight size={16} strokeWidth={1.5} /></Link></div>
          </Reveal>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
